// A production's board as one read (`api.getProjectBoard`) turned into the maps the project view keeps.
// Before the board read existed the view asked per episode, per scene and per shot; the maps are the same.
import { compareCodes } from "./lib.ts";
import { isNamedScene } from "./scenes.ts";
import type { Episode, ProjectBoard, Scene, Shot, TakesResponse } from "./types.ts";

export interface ShotHome {
  sceneId: string;
  episodeId: string;
}

export interface BoardMaps {
  episodes: Episode[];
  /** per episode, in board order */
  scenes: Map<string, Scene[]>;
  /** per scene, by shot code */
  shots: Map<string, Shot[]>;
  shotHome: Map<string, ShotHome>;
  shotById: Map<string, Shot>;
  /** per shot */
  takes: Map<string, TakesResponse>;
  /** per scene */
  sceneTakes: Map<string, TakesResponse>;
}

export function boardMaps(b: ProjectBoard): BoardMaps {
  const episodes = [...b.episodes].sort((a, c) => a.number - c.number);
  const scenes = new Map<string, Scene[]>(episodes.map((e) => [e.id, []]));
  for (const s of b.scenes) scenes.get(s.episode_id)?.push(s);
  for (const list of scenes.values()) list.sort((a, c) => a.position - c.position);
  const episodeOf = new Map<string, string>();
  for (const [eid, list] of scenes) for (const s of list) episodeOf.set(s.id, eid);

  const shotRow = new Map(b.shots.map((s) => [s.id, s]));
  const shots = new Map<string, Shot[]>([...episodeOf.keys()].map((id) => [id, []]));
  for (const p of b.placements) {
    const s = shotRow.get(p.shot_id);
    if (s) shots.get(p.scene_id)?.push(s);
  }
  const shotHome = new Map<string, ShotHome>();
  const shotById = new Map<string, Shot>();
  const sceneById = new Map(b.scenes.map((s) => [s.id, s]));
  for (const [eid, list] of scenes) {
    for (const here of list) {
      const sorted = (shots.get(here.id) ?? []).sort((a, c) => compareCodes(a.code, c.code));
      for (const s of sorted) {
        // A shot shared by an unnamed and a named scene is at home in the named one.
        const home = shotHome.get(s.id);
        const homeScene = home ? sceneById.get(home.sceneId) : undefined;
        if (!home || (homeScene && !isNamedScene(homeScene) && isNamedScene(here)))
          shotHome.set(s.id, { sceneId: here.id, episodeId: eid });
        shotById.set(s.id, s);
      }
    }
  }

  const takes = new Map<string, TakesResponse>();
  for (const id of shotById.keys()) takes.set(id, { items: [], next_cursor: null, current_selection: [] });
  const sceneTakes = new Map<string, TakesResponse>();
  for (const id of episodeOf.keys())
    sceneTakes.set(id, { items: [], next_cursor: null, current_selection: [] });
  for (const t of b.takes) {
    if (t.shot_id) takes.get(t.shot_id)?.items.push(t);
    if (t.scene_id) sceneTakes.get(t.scene_id)?.items.push(t);
  }
  for (const s of b.shot_selection) if (s.shot_id) takes.get(s.shot_id)?.current_selection.push(s);
  for (const s of b.scene_selection) if (s.scene_id) sceneTakes.get(s.scene_id)?.current_selection.push(s);

  return { episodes, scenes, shots, shotHome, shotById, takes, sceneTakes };
}
